import React, {ReactElement} from 'react'
import {useHistory} from 'react-router-dom'
import {useDispatch} from 'react-redux'

import {AppearanceType} from 'types/Button'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {
  trackEvent,
  trackUserAction
} from '@daedalus/core/src/analytics/modules/actions'
import {
  AuthContextType,
  SourceComponentType,
  TrackUserSignInClickSourceComponent,
  AuthenticationFlows
} from '@daedalus/core/src/auth/types/Auth'
import {
  trackLoginClick,
  trackUserSignInClick
} from '@daedalus/core/src/auth/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import Button from '../../../../designSystem/Button'

interface ButtonProps {
  isMemberPlus?: boolean
  children: ReactElement
  onClick: () => void
}

const ButtonElement = ({isMemberPlus, children, onClick}: ButtonProps) => {
  const appearance: AppearanceType = isMemberPlus ? 'privateDeal' : 'primary'

  return (
    <Button fullWidth appearance={appearance} size="lg" onClick={onClick}>
      {children}
    </Button>
  )
}

interface PropsType {
  isAuthenticated: boolean
  isMemberPlus: boolean
  logIn: AuthContextType['logIn']
  anonymousId: string
}

export const ExploreButton = ({
  isAuthenticated,
  isMemberPlus,
  logIn,
  anonymousId
}: PropsType) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const handleButtonClick = () => {
    dispatch(
      trackUserAction(
        'Click',
        'MembershipPage',
        isMemberPlus ? 'ExplorePlusDealsButton' : 'StartExploringButton'
      )
    )
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.MembershipExplore,
        action: Action.Clicked,
        payload: {
          version: isMemberPlus ? 'plus' : 'member'
        },
        team: Team.Retention
      })
    )

    history.push('/')
  }

  const handleUnauthenticatedClick = () => {
    dispatch(trackUserAction('Click', 'MembershipPage', 'JoinToSaveButton'))
    const component = TrackUserSignInClickSourceComponent.MembershipPage
    dispatch(trackUserSignInClick(component))
    dispatch(trackLoginClick(SourceComponentType.MembershipPage))
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.MembershipExplore,
        action: Action.Clicked,
        payload: {
          version: 'guest'
        },
        team: Team.Retention
      })
    )

    logIn({
      sourceComponent: component,
      flow: AuthenticationFlows.SignUp,
      anonymousId
    })
  }

  if (isMemberPlus) {
    return (
      <ButtonElement isMemberPlus={isMemberPlus} onClick={handleButtonClick}>
        <FormattedMessageWrapper
          id="membership.exploreDeals"
          defaultMessage="Explore plus deals"
        />
      </ButtonElement>
    )
  }

  if (isAuthenticated) {
    return (
      <ButtonElement onClick={handleButtonClick}>
        <FormattedMessageWrapper
          id="membership.startExploring"
          defaultMessage="Start exploring"
        />
      </ButtonElement>
    )
  }

  return (
    <ButtonElement onClick={handleUnauthenticatedClick}>
      <FormattedMessageWrapper
        id="membership.joinToSave"
        defaultMessage="Join to start saving"
      />
    </ButtonElement>
  )
}
